import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

const CATEGORIES = [
  { key: 'all', label: 'All', icon: 'Bell' },
  { key: 'system', label: 'System', icon: 'Settings' },
  { key: 'ticket', label: 'Tickets', icon: 'ClipboardList' },
  { key: 'quote', label: 'Quotes', icon: 'FileText' },
  { key: 'assignment', label: 'Assignments', icon: 'UserCog' },
  { key: 'invoice', label: 'Invoices', icon: 'DollarSign' },
];

const CATEGORY_COLORS = {
  system: 'bg-blue-100 text-blue-700',
  ticket: 'bg-orange-100 text-orange-700',
  quote: 'bg-green-100 text-green-700',
  assignment: 'bg-purple-100 text-purple-700',
  invoice: 'bg-emerald-100 text-emerald-700',
};

const CATEGORY_ICONS = {
  system: 'Settings',
  ticket: 'ClipboardList',
  quote: 'FileText',
  assignment: 'UserCog',
  invoice: 'DollarSign',
};

const NotificationCenter = ({ isOpen, onClose, onUnreadCountChange }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const panelRef = useRef(null);

  const [notifications, setNotifications] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [loading, setLoading] = useState(false);
  const [markingAll, setMarkingAll] = useState(false);

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      let query = supabase?.from('notifications')?.select('*')?.order('created_at', { ascending: false })?.limit(50);

      if (user?.id) {
        query = query?.eq('user_id', user?.id);
      }

      const { data, error } = await query;
      if (!error && data) {
        setNotifications(data);
        const unread = data?.filter(n => !n?.is_read)?.length;
        onUnreadCountChange?.(unread);
      }
    } catch (err) {
      console.error('Error fetching notifications:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.id, onUnreadCountChange]);

  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen, fetchNotifications]);

  // Real-time subscription
  useEffect(() => {
    if (!user?.id) return;

    const channel = supabase?.channel('notifications-realtime')?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user?.id}`,
        },
        (payload) => {
          if (payload?.eventType === 'INSERT') {
            setNotifications(prev => {
              const updated = [payload?.new, ...prev];
              const unread = updated?.filter(n => !n?.is_read)?.length;
              onUnreadCountChange?.(unread);
              return updated;
            });
          } else if (payload?.eventType === 'UPDATE') {
            setNotifications(prev => {
              const updated = prev?.map(n => n?.id === payload?.new?.id ? payload?.new : n);
              const unread = updated?.filter(n => !n?.is_read)?.length;
              onUnreadCountChange?.(unread);
              return updated;
            });
          } else if (payload?.eventType === 'DELETE') {
            setNotifications(prev => {
              const updated = prev?.filter(n => n?.id !== payload?.old?.id);
              const unread = updated?.filter(n => !n?.is_read)?.length;
              onUnreadCountChange?.(unread);
              return updated;
            });
          }
        }
      )?.subscribe();

    return () => {
      supabase?.removeChannel(channel);
    };
  }, [user?.id, onUnreadCountChange]);

  // Initial unread count fetch (even when panel is closed)
  useEffect(() => {
    const fetchUnreadCount = async () => {
      if (!user?.id) return;
      try {
        const { count } = await supabase?.from('notifications')?.select('id', { count: 'exact', head: true })?.eq('user_id', user?.id)?.eq('is_read', false);
        onUnreadCountChange?.(count || 0);
      } catch (err) {
        console.error('Error fetching unread count:', err);
      }
    };
    fetchUnreadCount();
  }, [user?.id, onUnreadCountChange]);

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (panelRef?.current && !panelRef?.current?.contains(e?.target)) {
        onClose?.();
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  const markAsRead = async (notificationId) => {
    try {
      await supabase?.from('notifications')?.update({ is_read: true })?.eq('id', notificationId);
      setNotifications(prev => {
        const updated = prev?.map(n => n?.id === notificationId ? { ...n, is_read: true } : n);
        const unread = updated?.filter(n => !n?.is_read)?.length;
        onUnreadCountChange?.(unread);
        return updated;
      });
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  };

  const markAllAsRead = async () => {
    setMarkingAll(true);
    try {
      const unreadIds = notifications?.filter(n => !n?.is_read)?.map(n => n?.id);
      if (unreadIds?.length === 0) return;
      await supabase?.from('notifications')?.update({ is_read: true })?.in('id', unreadIds);
      setNotifications(prev => prev?.map(n => ({ ...n, is_read: true })));
      onUnreadCountChange?.(0);
    } catch (err) {
      console.error('Error marking all as read:', err);
    } finally {
      setMarkingAll(false);
    }
  };

  const handleNotificationClick = async (notification) => {
    if (!notification?.is_read) {
      await markAsRead(notification?.id);
    }
    if (notification?.link) {
      navigate(notification?.link);
      onClose?.();
    }
  };

  const filteredNotifications = activeCategory === 'all'
    ? notifications
    : notifications?.filter(n => n?.category === activeCategory);

  const unreadCount = notifications?.filter(n => !n?.is_read)?.length;

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date?.toLocaleDateString();
  };

  if (!isOpen) return null;

  return (
    <div
      ref={panelRef}
      className="absolute top-full right-0 mt-2 w-96 bg-popover rounded-xl shadow-elevation-3 z-[1200] flex flex-col overflow-hidden border border-border"
      style={{ maxHeight: '80vh' }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-card">
        <div className="flex items-center gap-2">
          <Icon name="Bell" size={18} color="var(--color-primary)" />
          <span className="font-semibold text-foreground text-sm">Notifications</span>
          {unreadCount > 0 && (
            <span className="bg-accent text-accent-foreground text-xs font-bold px-2 py-0.5 rounded-full">
              {unreadCount}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {unreadCount > 0 && (
            <button
              onClick={markAllAsRead}
              disabled={markingAll}
              className="text-xs text-primary hover:text-primary/80 font-medium transition-colors disabled:opacity-50"
            >
              {markingAll ? 'Marking...' : 'Mark all read'}
            </button>
          )}
          <button
            onClick={onClose}
            className="p-1 rounded-md hover:bg-muted transition-colors"
          >
            <Icon name="X" size={16} color="var(--color-muted-foreground)" />
          </button>
        </div>
      </div>
      {/* Category Tabs */}
      <div className="flex items-center gap-1 px-3 py-2 border-b border-border bg-card overflow-x-auto">
        {CATEGORIES?.map(cat => {
          const catCount = cat?.key === 'all'
            ? notifications?.filter(n => !n?.is_read)?.length
            : notifications?.filter(n => n?.category === cat?.key && !n?.is_read)?.length;
          return (
            <button
              key={cat?.key}
              onClick={() => setActiveCategory(cat?.key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${
                activeCategory === cat?.key
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              }`}
            >
              <Icon name={cat?.icon} size={12} color="currentColor" />
              {cat?.label}
              {catCount > 0 && (
                <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full ${
                  activeCategory === cat?.key
                    ? 'bg-primary-foreground/20 text-primary-foreground'
                    : 'bg-accent text-accent-foreground'
                }`}>
                  {catCount}
                </span>
              )}
            </button>
          );
        })}
      </div>
      {/* Notification List */}
      <div className="overflow-y-auto flex-1">
        {loading ? (
          <div className="flex items-center justify-center py-10">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
          </div>
        ) : filteredNotifications?.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center px-4">
            <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center mb-3">
              <Icon name="BellOff" size={20} color="var(--color-muted-foreground)" />
            </div>
            <p className="text-sm font-medium text-foreground">No notifications</p>
            <p className="text-xs text-muted-foreground mt-1">You're all caught up!</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {filteredNotifications?.map(notification => (
              <div
                key={notification?.id}
                onClick={() => handleNotificationClick(notification)}
                className={`flex gap-3 px-4 py-3 cursor-pointer transition-colors hover:bg-muted/50 ${
                  !notification?.is_read ? 'bg-primary/5' : ''
                }`}
              >
                {/* Category Icon */}
                <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center mt-0.5 ${
                  CATEGORY_COLORS?.[notification?.category] || 'bg-gray-100 text-gray-600'
                }`}>
                  <Icon
                    name={CATEGORY_ICONS?.[notification?.category] || 'Bell'}
                    size={14}
                    color="currentColor"
                  />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <p className={`text-sm leading-snug ${
                      !notification?.is_read ? 'font-semibold text-foreground' : 'font-medium text-foreground/80'
                    }`}>
                      {notification?.title}
                    </p>
                    {!notification?.is_read && (
                      <span className="flex-shrink-0 w-2 h-2 bg-primary rounded-full mt-1.5"></span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                    {notification?.message}
                  </p>
                  <div className="flex items-center justify-between mt-1.5">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      CATEGORY_COLORS?.[notification?.category] || 'bg-gray-100 text-gray-600'
                    }`}>
                      {notification?.category}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {formatTime(notification?.created_at)}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationCenter;
